import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  Typography,
  CircularProgress,
  Divider,
  Badge,
  MenuItem,
  Card,
  CardContent,
  CardActions,
  ToggleButton,
  ToggleButtonGroup,
  Stack,
  TablePagination,
  Paper,
  Backdrop,
  IconButton,
  InputAdornment,
} from '@mui/material';
import { GridColDef, GridPaginationModel } from '@mui/x-data-grid';
import { ReusableDataGrid, CustomDrawer, ActionsMenu, type ActionMenuItem } from '@/components/common';
import {
  FilterList as FilterIcon,
  ViewList as ViewListIcon,
  ViewModule as ViewModuleIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Restore as RestoreIcon,
  Lock as LockIcon,
  LockOpen as LockOpenIcon,
  VpnKey as VpnKeyIcon,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { RootState, AppDispatch } from '@/store/types';
import {
  fetchUsers,
  createUser,
  updateUser,
  deleteUser,
  updateUserStatus,
  updateUserPassword,
  restoreUser,
  fetchCompanies,
} from '@/store';
import { UserFormData } from '@/services/user/userService';

const passwordChangeValidationSchema = Yup.object({
  password: Yup.string().required('Password is required').min(6, 'Password must be at least 6 characters'),
  confirmPassword: Yup.string()
    .required('Confirm password is required')
    .oneOf([Yup.ref('password')], 'Passwords must match'),
});

const initialFormData: UserFormData = {
  name: '',
  empId: '',
  companyId: '',
  role: 'employee',
  email: '',
  mobile: '',
  password: '',
  confirmPassword: '',
  isActive: true,
  remarks: '',
};

export const ManageEmployee: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { users, usersLoading, isLoading, pagination } = useSelector((state: RootState) => state.user);
  const { companies } = useSelector((state: RootState) => state.company);

  const [openDrawer, setOpenDrawer] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [restoreConfirmId, setRestoreConfirmId] = useState<string | null>(null);
  const [statusToggleConfirm, setStatusToggleConfirm] = useState<{ id: string; currentStatus: boolean } | null>(null);
  const [passwordChangeDrawerOpen, setPasswordChangeDrawerOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [roleFilter, setRoleFilter] = useState<string>('');
  const [isActiveFilter, setIsActiveFilter] = useState<boolean | ''>('');
  const [companyIdFilter, setCompanyIdFilter] = useState<string>('');
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [viewMode, setViewMode] = useState<'table' | 'cards'>('table');
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 10,
  });
  const [apiLoading, setApiLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [showChangeConfirmPassword, setShowChangeConfirmPassword] = useState(false);

  const formik = useFormik({
    initialValues: initialFormData,
    validationSchema: Yup.object({
      name: Yup.string().required('Name is required').min(2, 'Name must be at least 2 characters'),
      empId: Yup.string().required('Employee ID is required').min(3, 'Employee ID must be at least 3 characters'),
      companyId: Yup.string().required('Company name is required').min(2, 'Company name must be at least 2 characters'),
      role: Yup.string().oneOf(['admin', 'manager', 'employee'], 'Invalid role').required('Role is required'),
      email: Yup.string().email('Invalid email').required('Email is required'),
      mobile: Yup.string()
        .required('Mobile is required')
        .matches(/^\+?[1-9]\d{9,14}$/, 'Invalid mobile number'),
      password: !editingId
        ? Yup.string().required('Password is required').min(6, 'Password must be at least 6 characters')
        : Yup.string(),
      confirmPassword: !editingId
        ? Yup.string()
            .required('Confirm password is required')
            .oneOf([Yup.ref('password')], 'Passwords must match')
        : Yup.string(),
      isActive: Yup.boolean(),
      remarks: Yup.string(),
    }),
    enableReinitialize: true,
    onSubmit: async (values) => {
      try {
        const dataToSubmit = { ...values };

        // Remove confirmPassword before submitting
        delete dataToSubmit.confirmPassword;

        if (editingId && !dataToSubmit.password) {
          delete dataToSubmit.password;
        }

        if (editingId) {
          await dispatch(updateUser({
            id: editingId,
            data: dataToSubmit
          }));
        } else {
          await dispatch(createUser(dataToSubmit));
        }
        handleCloseDrawer();
        loadData();
      } catch (error) {
        console.error('Error saving user:', error);
      }
    },
  });

  const passwordFormik = useFormik({
    initialValues: { password: '', confirmPassword: '' },
    validationSchema: passwordChangeValidationSchema,
    onSubmit: async (values) => {
      if (selectedUserId) {
        try {
          setApiLoading(true);
          await dispatch(updateUserPassword({
            id: selectedUserId,
            data: { password: values.password }
          }));
          setPasswordChangeDrawerOpen(false);
          passwordFormik.resetForm();
          setSelectedUserId(null);
        } catch (error) {
          console.error('Error updating password:', error);
        } finally {
          setApiLoading(false);
        }
      }
    },
  });

  const loadData = useCallback(() => {
    const params: any = {
      page: paginationModel.page + 1,
      limit: paginationModel.pageSize,
    };

    if (roleFilter) params.role = roleFilter;
    if (isActiveFilter !== '') params.isActive = isActiveFilter;
    if (companyIdFilter) params.companyId = companyIdFilter;

    dispatch(fetchUsers(params));
  }, [dispatch, paginationModel, roleFilter, isActiveFilter, companyIdFilter]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    dispatch(fetchCompanies({ page: 1, limit: 100 }));
  }, [dispatch]);

  const handleOpenDrawer = (userId?: string) => {
    if (userId) {
      const userToEdit = users.find(u => u._id === userId);
      if (userToEdit) {
        setEditingId(userId);
        formik.setValues({
          name: userToEdit.name,
          empId: userToEdit.empId,
          companyId: typeof userToEdit.companyId === 'object' ? userToEdit.companyId._id : userToEdit.companyId || '',
          role: userToEdit.role,
          email: userToEdit.email,
          mobile: userToEdit.mobile,
          password: '',
          confirmPassword: '',
          isActive: userToEdit.isActive,
          remarks: userToEdit.remarks || '',
        });
      }
    } else {
      setEditingId(null);
      formik.resetForm();
    }
    setOpenDrawer(true);
  };

  const handleCloseDrawer = () => {
    setOpenDrawer(false);
    setEditingId(null);
    formik.resetForm();
  };

  const handleDelete = async (userId: string) => {
    try {
      setApiLoading(true);
      await dispatch(deleteUser(userId));
      setDeleteConfirmId(null);
      loadData();
    } catch (error) {
      console.error('Error deleting user:', error);
    } finally {
      setApiLoading(false);
    }
  };

  const handleToggleStatus = async () => {
    if (statusToggleConfirm) {
      try {
        setApiLoading(true);
        await dispatch(updateUserStatus({
          id: statusToggleConfirm.id,
          data: { isActive: !statusToggleConfirm.currentStatus }
        }));
        setStatusToggleConfirm(null);
        loadData();
      } catch (error) {
        console.error('Error toggling user status:', error);
      } finally {
        setApiLoading(false);
      }
    }
  };

  const handleRestoreUser = async () => {
    if (restoreConfirmId) {
      try {
        setApiLoading(true);
        await dispatch(restoreUser(restoreConfirmId));
        setRestoreConfirmId(null);
        loadData();
      } catch (error) {
        console.error('Error restoring user:', error);
      } finally {
        setApiLoading(false);
      }
    }
  };

  const handleChangePassword = (userId: string) => {
    setSelectedUserId(userId);
    setPasswordChangeDrawerOpen(true);
  };

  const handlePaginationModelChange = (newModel: GridPaginationModel) => {
    setPaginationModel(newModel);
  };

  const handleApplyFilters = () => {
    setPaginationModel({ ...paginationModel, page: 0 });
    setFilterDrawerOpen(false);
  };

  const handleClearFilters = () => {
    setRoleFilter('');
    setIsActiveFilter('');
    setCompanyIdFilter('');
    setPaginationModel({ ...paginationModel, page: 0 });
  };

  const activeFilterCount = [roleFilter, isActiveFilter, companyIdFilter].filter(f => f !== '' && f !== undefined).length;

  const getActionsMenuItems = (userId: string): ActionMenuItem[] => {
    const userItem = users.find(u => u._id === userId);
    if (!userItem) return [];

    const items: ActionMenuItem[] = [
      {
        id: `edit-${userId}`,
        title: 'Edit',
        icon: <EditIcon fontSize="small" />,
        onClick: () => handleOpenDrawer(userId),
        disabled: userItem.isDeleted,
      },
    ];

    if (userItem.isDeleted) {
      items.push({
        id: `restore-${userId}`,
        title: 'Restore',
        icon: <RestoreIcon fontSize="small" />,
        onClick: () => setRestoreConfirmId(userId),
      });
    } else {
      items.push(
        {
          id: `toggle-status-${userId}`,
          title: userItem.isActive ? 'Deactivate' : 'Activate',
          icon: userItem.isActive ? <LockIcon fontSize="small" /> : <LockOpenIcon fontSize="small" />,
          onClick: () => setStatusToggleConfirm({ id: userId, currentStatus: userItem.isActive }),
        },
        {
          id: `change-password-${userId}`,
          title: 'Change Password',
          icon: <VpnKeyIcon fontSize="small" />,
          onClick: () => handleChangePassword(userId),
        },
        {
          id: `delete-${userId}`,
          title: 'Delete',
          icon: <DeleteIcon fontSize="small" />,
          onClick: () => setDeleteConfirmId(userId),
          color: 'error',
        }
      );
    }

    return items;
  };

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Name / Emp ID',
      flex: 1.2,
      minWidth: 180,
      renderCell: (params) => (
        <Box>
          <Typography variant="body2" fontWeight="medium">
            {params.row.name}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {params.row.empId}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'email',
      headerName: 'Email',
      flex: 1.2,
      minWidth: 200,
    },
    {
      field: 'mobile',
      headerName: 'Mobile',
      flex: 1,
      minWidth: 140,
    },
    {
      field: 'role',
      headerName: 'Role',
      flex: 0.8,
      minWidth: 100,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          sx={{textTransform:'capitalize'}}
          color={
            params.value === 'admin' ? 'error' :
            params.value === 'manager' ? 'warning' :
            params.value === 'employee' ? 'secondary' : 'default'
          }
        />
      ),
    },
    {
      field: 'companyId',
      headerName: 'Company',
      flex: 1,
      minWidth: 150,
      valueGetter: (value: any) => {
        if (typeof value === 'object' && value !== null && 'name' in value) {
          return value.name;
        }
        return 'N/A';
      },
    },
    {
      field: 'isActive',
      headerName: 'Status',
      flex: 0.8,
      minWidth: 100,
      renderCell: (params) => (
        <Chip
          label={params.row.isDeleted ? 'Deleted' : params.value ? 'Active' : 'Inactive'}
          size="small"
          color={params.row.isDeleted ? 'default' : params.value ? 'success' : 'warning'}
        />
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 0.6,
      minWidth: 100,
      sortable: false,
      renderCell: (params) => (
        <ActionsMenu actions={getActionsMenuItems(params.row._id)} />
      ),
    },
  ];

  return (
    <Box>
      <Box mb={3}>
        <Box display="flex" alignItems="center" mb={2}>
          <Typography variant="h5" component="h5" flexGrow={1}>
            Manage Employees
          </Typography>
        </Box>
        <Divider />
      </Box>

      <Grid container spacing={2} alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
       <Grid size="auto">
       {false &&    <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(_, newMode) => newMode && setViewMode(newMode)}
            size="small"
          >
            <ToggleButton value="table" aria-label="table view">
              <ViewListIcon fontSize="small" />
              <Typography variant="body2" sx={{ ml: 1 }}>Table</Typography>
            </ToggleButton>
            <ToggleButton value="cards" aria-label="cards view">
              <ViewModuleIcon fontSize="small" />
              <Typography variant="body2" sx={{ ml: 1 }}>Cards</Typography>
            </ToggleButton>
          </ToggleButtonGroup>}
        </Grid>
        <Grid size="auto">
          <Stack direction="row" spacing={2}>
            <Badge badgeContent={activeFilterCount} color="primary">
              <Button
                variant="outlined"
                startIcon={<FilterIcon />}
                onClick={() => setFilterDrawerOpen(true)}
              >
                Filters
              </Button>
            </Badge>
            <Button
              variant="contained"
              onClick={() => handleOpenDrawer()}
              disabled={isLoading}
            >
              Add Employee
            </Button>
          </Stack>
        </Grid>
      </Grid>

      {viewMode === 'table' ? (
        // <Box sx={{ height: 600, width: '100%' }}>
          <ReusableDataGrid
            rows={users}
            columns={columns}
            loading={usersLoading}
            paginationModel={paginationModel}
            onPaginationModelChange={handlePaginationModelChange}
            paginationData={pagination ? { totalRecords: pagination.totalItems } : undefined}
            enableServerSidePagination={true}
            getRowId={(row) => row._id}
          />
        // </Box>
      ) : (
        <Box>
          <Grid container spacing={2}>
            {usersLoading ? (
              <Grid size={12} display="flex" justifyContent="center" py={4}>
                <CircularProgress />
              </Grid>
            ) : users.length === 0 ? (
              <Grid size={12}>
                <Paper sx={{ p: 3, textAlign: 'center' }}>
                  <Typography variant="body1" color="text.secondary">
                    No employees found
                  </Typography>
                </Paper>
              </Grid>
            ) : (
              users.map((user) => (
                <Grid key={user._id} size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                  <Card>
                    <CardContent>
                      <Box display="flex" justifyContent="space-between" alignItems="start" mb={2}>
                        <Typography variant="h6" component="div" noWrap>
                          {user.name}
                        </Typography>
                        <Chip
                          label={user.isDeleted ? 'Deleted' : user.isActive ? 'Active' : 'Inactive'}
                          size="small"
                          color={user.isDeleted ? 'default' : user.isActive ? 'success' : 'warning'}
                        />
                      </Box>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        ID: {user.empId}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" gutterBottom noWrap>
                        {user.email}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        {user.mobile}
                      </Typography>
                      <Box mt={1}>
                        <Chip
                          label={user.role}
                          size="small"
                          color={
                            user.role === 'admin' ? 'error' :
                            user.role === 'manager' ? 'warning' : 'default'
                          }
                        />
                      </Box>
                      {typeof user.companyId === 'object' && user.companyId && (
                        <Typography variant="body2" color="text.secondary" mt={1} noWrap>
                          Company: {user.companyId.name}
                        </Typography>
                      )}
                    </CardContent>
                    <CardActions>
                      <ActionsMenu actions={getActionsMenuItems(user._id)} />
                    </CardActions>
                  </Card>
                </Grid>
              ))
            )}
          </Grid>
          {pagination && (
            <Box display="flex" justifyContent="center" mt={3}>
              <TablePagination
                component="div"
                count={pagination.totalItems}
                page={paginationModel.page}
                onPageChange={(_, newPage) => setPaginationModel({ ...paginationModel, page: newPage })}
                rowsPerPage={paginationModel.pageSize}
                onRowsPerPageChange={(e) => setPaginationModel({ page: 0, pageSize: parseInt(e.target.value, 10) })}
                rowsPerPageOptions={[5, 10, 25, 50]}
              />
            </Box>
          )}
        </Box>
      )}

      <CustomDrawer
        open={openDrawer}
        onClose={handleCloseDrawer}
        title={editingId ? 'Edit Employee' : 'Add Employee'}
        width={'100%'}
        disableBackdropClick={true}
      >
        {/* <form onSubmit={formik.handleSubmit}> */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width:'100%' }}>
            <TextField
              fullWidth
              required
              name="name"
              label="Name"
              value={formik.values.name}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.name && Boolean(formik.errors.name)}
              helperText={formik.touched.name && formik.errors.name}
              variant="outlined"
            />

            <TextField
              fullWidth
              required
              name="empId"
              label="Employee ID"
              value={formik.values.empId}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.empId && Boolean(formik.errors.empId)}
              helperText={formik.touched.empId && formik.errors.empId}
              variant="outlined"
            />

            <TextField
              fullWidth
              required
              select
              name="role"
              label="Role"
              value={formik.values.role}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.role && Boolean(formik.errors.role)}
              helperText={formik.touched.role && formik.errors.role}
              variant="outlined"
            >
              <MenuItem value="employee">Employee</MenuItem>
              <MenuItem value="manager">Manager</MenuItem>
              <MenuItem value="admin">Admin</MenuItem>
            </TextField>

            <TextField
              fullWidth
              select
              required
              name="companyId"
              label="Company Name"
              value={formik.values.companyId}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.companyId && Boolean(formik.errors.companyId)}
              helperText={formik.touched.companyId && formik.errors.companyId}
              variant="outlined"
            >
              <MenuItem value="">None</MenuItem>
              {companies.map((company) => (
                <MenuItem key={company._id} value={company._id}>
                  {company.name}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              fullWidth
              required
              name="email"
              label="Email"
              type="email"
              value={formik.values.email}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.email && Boolean(formik.errors.email)}
              helperText={formik.touched.email && formik.errors.email}
              variant="outlined"
            />

            <TextField
              fullWidth
              required
              name="mobile"
              label="Mobile"
              value={formik.values.mobile}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.mobile && Boolean(formik.errors.mobile)}
              helperText={formik.touched.mobile && formik.errors.mobile}
              variant="outlined"
            />

            {!editingId && (
              <>
                <TextField
                  fullWidth
                  required
                  name="password"
                  label="Password"
                  type={showPassword ? 'text' : 'password'}
                  value={formik.values.password}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.password && Boolean(formik.errors.password)}
                  helperText={formik.touched.password && formik.errors.password}
                  variant="outlined"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <TextField
                  fullWidth
                  required
                  name="confirmPassword"
                  label="Confirm Password"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={formik.values.confirmPassword}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.confirmPassword && Boolean(formik.errors.confirmPassword)}
                  helperText={formik.touched.confirmPassword && formik.errors.confirmPassword}
                  variant="outlined"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle confirm password visibility"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          edge="end"
                        >
                          {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </>
            )}

            <TextField
              fullWidth
              select
              name="isActive"
              label="Status"
              value={formik.values.isActive}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.isActive && Boolean(formik.errors.isActive)}
              helperText={formik.touched.isActive && formik.errors.isActive}
              variant="outlined"
            >
              <MenuItem value="true">Active</MenuItem>
              <MenuItem value="false">Inactive</MenuItem>
            </TextField>

            <TextField
              fullWidth
              name="remarks"
              label="Remarks"
              multiline
              rows={3}
              value={formik.values.remarks}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.remarks && Boolean(formik.errors.remarks)}
              helperText={formik.touched.remarks && formik.errors.remarks}
              variant="outlined"
            />

            <Box display="flex" gap={2} justifyContent="flex-end" mt={2}>
              <Button onClick={handleCloseDrawer} variant="outlined">
                Cancel
              </Button>
              <Button               onClick={(e:any) => formik.handleSubmit(e)}
 variant="contained" disabled={isLoading}>
                {editingId ? 'Update' : 'Create'}
              </Button>
            </Box>
          </Box>
        {/* </form> */}
      </CustomDrawer>

      <Dialog open={!!deleteConfirmId} onClose={() => setDeleteConfirmId(null)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this employee? This action will mark them as deleted.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmId(null)}>Cancel</Button>
          <Button onClick={() => deleteConfirmId && handleDelete(deleteConfirmId)} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!statusToggleConfirm} onClose={() => setStatusToggleConfirm(null)}>
        <DialogTitle>Confirm Status Change</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to {statusToggleConfirm?.currentStatus ? 'deactivate' : 'activate'} this employee?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatusToggleConfirm(null)}>Cancel</Button>
          <Button onClick={handleToggleStatus} color="primary" variant="contained">
            {statusToggleConfirm?.currentStatus ? 'Deactivate' : 'Activate'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!restoreConfirmId} onClose={() => setRestoreConfirmId(null)}>
        <DialogTitle>Confirm Restore</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to restore this employee? This will make them active again.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRestoreConfirmId(null)}>Cancel</Button>
          <Button onClick={handleRestoreUser} color="success" variant="contained">
            Restore
          </Button>
        </DialogActions>
      </Dialog>

      <CustomDrawer
        open={passwordChangeDrawerOpen}
        disableBackdropClick={true}

        onClose={() => {
          setPasswordChangeDrawerOpen(false);
          passwordFormik.resetForm();
          setSelectedUserId(null);
        }}
        title="Change Password"
          width={'100%'}
      >
        <form onSubmit={passwordFormik.handleSubmit}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width:'100%' }}>
            <TextField
              fullWidth
              required
              name="password"
              label="New Password"
              type={showChangePassword ? 'text' : 'password'}
              value={passwordFormik.values.password}
              onChange={passwordFormik.handleChange}
              onBlur={passwordFormik.handleBlur}
              error={passwordFormik.touched.password && Boolean(passwordFormik.errors.password)}
              helperText={passwordFormik.touched.password && passwordFormik.errors.password}
              variant="outlined"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowChangePassword(!showChangePassword)}
                      edge="end"
                    >
                      {showChangePassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              fullWidth
              required
              name="confirmPassword"
              label="Confirm New Password"
              type={showChangeConfirmPassword ? 'text' : 'password'}
              value={passwordFormik.values.confirmPassword}
              onChange={passwordFormik.handleChange}
              onBlur={passwordFormik.handleBlur}
              error={passwordFormik.touched.confirmPassword && Boolean(passwordFormik.errors.confirmPassword)}
              helperText={passwordFormik.touched.confirmPassword && passwordFormik.errors.confirmPassword}
              variant="outlined"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle confirm password visibility"
                      onClick={() => setShowChangeConfirmPassword(!showChangeConfirmPassword)}
                      edge="end"
                    >
                      {showChangeConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Box display="flex" gap={2} justifyContent="flex-end" mt={2}>
              <Button
                onClick={() => {
                  setPasswordChangeDrawerOpen(false);
                  passwordFormik.resetForm();
                  setSelectedUserId(null);
                }}
                variant="outlined"
              >
                Cancel
              </Button>
              <Button type="submit" variant="contained" disabled={apiLoading}>
                Update Password
              </Button>
            </Box>
          </Box>
        </form>
      </CustomDrawer>

      <CustomDrawer
        open={filterDrawerOpen}
        onClose={() => setFilterDrawerOpen(false)}
        title="Filters"
         width={'100%'}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            fullWidth
            select
            label="Role"
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            variant="outlined"
          >
            <MenuItem value="">All Roles</MenuItem>
            <MenuItem value="admin">Admin</MenuItem>
            <MenuItem value="manager">Manager</MenuItem>
            <MenuItem value="employee">Employee</MenuItem>
          </TextField>

          <TextField
            fullWidth
            select
            label="Company"
            value={companyIdFilter}
            onChange={(e) => setCompanyIdFilter(e.target.value)}
            variant="outlined"
          >
            <MenuItem value="">All Companies</MenuItem>
            {companies.map((company) => (
              <MenuItem key={company._id} value={company._id}>
                {company.name}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            fullWidth
            select
            label="Status"
            value={isActiveFilter}
            onChange={(e) => setIsActiveFilter(e.target.value === '' ? '' : e.target.value === 'true')}
            variant="outlined"
          >
            <MenuItem value="">All Status</MenuItem>
            <MenuItem value="true">Active</MenuItem>
            <MenuItem value="false">Inactive</MenuItem>
          </TextField>

          {activeFilterCount > 0 && (
            <Box mt={2}>
              <Typography variant="body2" color="text.secondary">
                {activeFilterCount} filter{activeFilterCount > 1 ? 's' : ''} applied
              </Typography>
            </Box>
          )}

          <Box display="flex" gap={2} mt={2}>
            <Button onClick={handleClearFilters} variant="outlined" fullWidth>
              Clear
            </Button>
            <Button onClick={handleApplyFilters} variant="contained" fullWidth>
              Apply
            </Button>
          </Box>
        </Box>
      </CustomDrawer>

      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.modal }}
        open={apiLoading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </Box>
  );
};
